import React, {useReducer, useRef, useImperativeHandle, forwardRef} from 'react'

import {
    Alert,
    Button,
    Modal,
    ModalActions,
    P,
    Popup
} from '../UI/index.js'
import {
    formSpacing,
    Editor,
    FormStack,
    Input,
    Select
} from '../UI/forms/index.js'

import {Reseller} from '../../../server/functions/resellers/resellers.types.js'
import {Parcel} from '../../../server/functions/parcels/parcels.types.js'
import Languages from '@utils/Languages.js'
import {emit} from 'src/socketio/socketEmit.js'
import type {PopupRef} from '../UI/overlays/Popup.js'

interface CustomerSupportModalProps {
    reseller: Reseller
    onChange: (parcel: Parcel) => void
}

interface CustomerSupportModalState {
    modalIsOpen: boolean
    parcel: Parcel | undefined
    name: string
    email: string
    reason: string
    body: string
    loading: boolean
    message: string
    error: string
}

export interface CustomerSupportModalHandle {
    open: (parcel: Parcel) => void
}

export const defaultCustomerSupportModalTitle = () => Languages.customerSupport['NL']

export const defaultCustomerSupportModalText = (reseller: Reseller) => `${Languages.customerSupportModalText['NL']} ${reseller.settings.accountName}`

// Empty (or missing, for resellers created before these settings existed) falls back to the default text
export const getCustomerSupportModalTitle = (reseller: Reseller) => reseller.settings.parcels.customerSupport?.modalTitle?.trim() || defaultCustomerSupportModalTitle()

export const getCustomerSupportModalText = (reseller: Reseller) => reseller.settings.parcels.customerSupport?.modalText?.trim() || defaultCustomerSupportModalText(reseller)

const CustomerSupportModal = forwardRef<CustomerSupportModalHandle, CustomerSupportModalProps>(({reseller, onChange}, ref) => {
    const initialState = {
        modalIsOpen: false,
        parcel: undefined as Parcel | undefined,
        name: '',
        email: '',
        reason: '',
        body: '',
        loading: false,
        message: '',
        error: ''
    }

    const [state, setState] = useReducer((state: CustomerSupportModalState, newState: Partial<CustomerSupportModalState>) => ({...state, ...newState}), structuredClone(initialState))

    const popupRef = useRef<PopupRef>(null)

    const open = (parcel: Parcel) => {
        setState({modalIsOpen: true, parcel})
    }

    const close = () => {
        setState(initialState)
    }

    useImperativeHandle(ref, () => ({
        open
    }))

    const handleSubmit = async () => {
        setState({loading: true, message: '', error: ''})

        try {
            const {data: parcel} = await emit<Parcel>('parcels.sendCustomerSupportEmail', {
                parcelId: state.parcel._id,
                name: state.name,
                email: state.email,
                reason: state.reason,
                message: state.body
            }) as {data: Parcel}

            onChange(parcel)

            close()
        } catch (err) {
            setState({loading: false, error: err.message})
        }
    }

    return (
        <Modal style={{width: 600}} show={state.modalIsOpen} onClose={close} title={getCustomerSupportModalTitle(reseller)}>

            <P style={{marginBottom: 24, whiteSpace: 'pre-wrap'}}>{getCustomerSupportModalText(reseller)}</P>

            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                <Input label={Languages.name['NL']} value={state.name} onChange={(e) => setState({name: e.target.value})} />
                <Input label={Languages.emailAddress['NL']} value={state.email} onChange={(e) => setState({email: e.target.value})} />
                <Select
                    label={Languages.supportReason['NL']}
                    value={state.reason}
                    options={reseller.settings.parcels.customerSupport.supportReasons.map((reason) => ({title: reason.name, value: reason.name}))}
                    onChange={(e) => setState({reason: e.target.value})}
                />

                <Editor
                    label={Languages.message['NL']}
                    value={state.body}
                    onChange={(e) => {
                        setState({body: e.target.value})
                    }
                    }
                />
            </FormStack>

            {state.error && <Alert style={{marginBottom: 6}} variant='danger'>{state.error}</Alert>}

            <ModalActions>
                <Button disabled={state.loading} onClick={close}>{Languages.cancel['NL']}</Button>
                <Button loading={state.loading} onClick={handleSubmit}>{Languages.send['NL']}</Button>
            </ModalActions>

            <Popup ref={popupRef} />
        </Modal>
    )
})

CustomerSupportModal.displayName = 'CustomerSupportModal'

export default CustomerSupportModal
